import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export class MigrationUtils {
  /**
   * Migrate existing shared watchlists to include isShared field
   * This function can be run once to update all existing data
   */
  static async migrateSharedWatchlists(): Promise<{
    success: boolean;
    migratedUsers: string[];
    errors: string[];
  }> {
    const migratedUsers: string[] = [];
    const errors: string[] = [];

    try {
      // 1. Get all active shared watchlists from sharedContent collection
      const sharedContentQuery = query(
        collection(db, 'sharedContent'),
        where('contentType', '==', 'watchlist'),
        where('isActive', '==', true)
      );
      
      const sharedContentSnapshot = await getDocs(sharedContentQuery);
      
      if (sharedContentSnapshot.empty) {
        console.log('No shared watchlists found to migrate');
        return { success: true, migratedUsers: [], errors: [] };
      }

      // Process each shared watchlist
      for (const sharedDoc of sharedContentSnapshot.docs) {
        const sharedData = sharedDoc.data();
        const userId = sharedData.userId;

        try {
          // 2. Update all watchlist entries for this user
          const watchlistQuery = query(
            collection(db, 'watchlist'),
            where('userId', '==', userId),
            where('isTracking', '!=', false) // Only update active entries
          );

          const watchlistSnapshot = await getDocs(watchlistQuery);

          if (!watchlistSnapshot.empty) {
            const batch = writeBatch(db);
            let batchCount = 0;

            watchlistSnapshot.forEach((watchlistDoc) => {
              // Skip if already has isShared field set to true
              if (watchlistDoc.data().isShared === true) {
                return;
              }

              batch.update(watchlistDoc.ref, {
                isShared: true,
                lastUpdated: serverTimestamp(),
              });
              batchCount++;

              // Firestore batch limit is 500 operations
              if (batchCount >= 500) {
                // Commit this batch and start a new one
                batch.commit();
                batchCount = 0;
              }
            });

            // Commit any remaining updates
            if (batchCount > 0) {
              await batch.commit();
            }

            migratedUsers.push(userId);
            console.log(`Migrated watchlist for user: ${userId}`);
          }
        } catch (error) {
          const errorMsg = `Error migrating user ${userId}: ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMsg);
          console.error(errorMsg);
        }
      }

      return {
        success: errors.length === 0,
        migratedUsers,
        errors,
      };
    } catch (error) {
      const errorMsg = `Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      errors.push(errorMsg);
      console.error(errorMsg);
      return {
        success: false,
        migratedUsers,
        errors,
      };
    }
  }

  /**
   * Clean up isShared field for unshared watchlists
   * This ensures data consistency
   */
  static async cleanupUnsharedWatchlists(): Promise<{
    success: boolean;
    cleanedUsers: string[];
    errors: string[];
  }> {
    const cleanedUsers: string[] = [];
    const errors: string[] = [];

    try {
      // Get all users who have watchlist entries with isShared=true
      const watchlistQuery = query(
        collection(db, 'watchlist'),
        where('isShared', '==', true)
      );

      const watchlistSnapshot = await getDocs(watchlistQuery);
      
      if (watchlistSnapshot.empty) {
        console.log('No watchlists marked as shared found');
        return { success: true, cleanedUsers: [], errors: [] };
      }

      // Group by userId
      const userIds = new Set<string>();
      watchlistSnapshot.forEach((doc) => {
        const userId = doc.data().userId;
        if (userId) {
          userIds.add(userId);
        }
      });

      // Check each user's sharing status
      for (const userId of Array.from(userIds)) {
        try {
          // Check if this user's watchlist is actually shared
          const sharedContentRef = doc(db, 'sharedContent', `watchlist_${userId}`);
          const sharedContentDoc = await getDoc(sharedContentRef);
          
          const isActuallyShared = sharedContentDoc.exists() && sharedContentDoc.data()?.isActive === true;

          if (!isActuallyShared) {
            // User's watchlist is not shared, update all their entries
            const userWatchlistQuery = query(
              collection(db, 'watchlist'),
              where('userId', '==', userId),
              where('isShared', '==', true)
            );

            const userWatchlistSnapshot = await getDocs(userWatchlistQuery);

            if (!userWatchlistSnapshot.empty) {
              const batch = writeBatch(db);
              let batchCount = 0;

              userWatchlistSnapshot.forEach((watchlistDoc) => {
                batch.update(watchlistDoc.ref, {
                  isShared: false,
                  lastUpdated: serverTimestamp(),
                });
                batchCount++;

                // Firestore batch limit is 500 operations
                if (batchCount >= 500) {
                  batch.commit();
                  batchCount = 0;
                }
              });

              // Commit any remaining updates
              if (batchCount > 0) {
                await batch.commit();
              }

              cleanedUsers.push(userId);
              console.log(`Cleaned up unshared watchlist for user: ${userId}`);
            }
          }
        } catch (error) {
          const errorMsg = `Error cleaning up user ${userId}: ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMsg);
          console.error(errorMsg);
        }
      }

      return {
        success: errors.length === 0,
        cleanedUsers,
        errors,
      };
    } catch (error) {
      const errorMsg = `Cleanup failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      errors.push(errorMsg);
      console.error(errorMsg);
      return {
        success: false,
        cleanedUsers,
        errors,
      };
    }
  }

  /**
   * Run all migration tasks
   */
  static async runAllMigrations(): Promise<{
    success: boolean;
    results: {
      sharedMigration: { migratedUsers: string[]; errors: string[] };
      cleanup: { cleanedUsers: string[]; errors: string[] };
    };
  }> {
    console.log('Starting watchlist sharing migrations...');

    // 1. Migrate shared watchlists
    console.log('Step 1: Migrating shared watchlists...');
    const sharedMigrationResult = await this.migrateSharedWatchlists();

    // 2. Clean up unshared watchlists
    console.log('Step 2: Cleaning up unshared watchlists...');
    const cleanupResult = await this.cleanupUnsharedWatchlists();

    const allErrors = [...sharedMigrationResult.errors, ...cleanupResult.errors];

    console.log('Migration completed.');
    console.log(`Migrated ${sharedMigrationResult.migratedUsers.length} shared watchlists`);
    console.log(`Cleaned up ${cleanupResult.cleanedUsers.length} unshared watchlists`);
    if (allErrors.length > 0) {
      console.error(`Encountered ${allErrors.length} errors during migration`);
    }

    return {
      success: allErrors.length === 0,
      results: {
        sharedMigration: {
          migratedUsers: sharedMigrationResult.migratedUsers,
          errors: sharedMigrationResult.errors,
        },
        cleanup: {
          cleanedUsers: cleanupResult.cleanedUsers,
          errors: cleanupResult.errors,
        },
      },
    };
  }
}